import { useCallback, useEffect, useState } from "react";
import { api } from "../api";
import type { LeadTask } from "../types";

/**
 * Задачи по клиенту.
 *
 * То, что модель вытащила из разговора как «что сделать», сервис заводит сюда
 * задачами. Дальше их ведёт менеджер: ставит срок, отмечает сделанное,
 * добавляет своё. Просроченное и сегодняшнее попадает в утреннюю сводку.
 */

/** «до 12 сентября», «сегодня», «просрочено на 3 дня» — коротко и по делу. */
function dueLabel(iso: string): { text: string; cls: string } {
  const due = new Date(iso);
  const today = new Date();
  const day = (d: Date) => Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  const diff = Math.round((day(due) - day(today)) / 86_400_000);

  if (diff === 0) return { text: "сегодня", cls: "amber" };
  if (diff === 1) return { text: "завтра", cls: "" };
  if (diff < 0) {
    const n = Math.abs(diff);
    const word = n === 1 ? "день" : n < 5 ? "дня" : "дней";
    return { text: `просрочено на ${n} ${word}`, cls: "red" };
  }
  return {
    text: `до ${due.toLocaleDateString("ru-RU", { day: "numeric", month: "long" })}`,
    cls: "",
  };
}

/** <input type="date"> хочет ГГГГ-ММ-ДД по местному времени. */
function toDateInput(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function LeadTasks({ meetingId }: { meetingId: number }) {
  const [tasks, setTasks] = useState<LeadTask[]>([]);
  const [text, setText] = useState("");
  const [due, setDue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [showDone, setShowDone] = useState(false);

  const load = useCallback(() => {
    api
      .leadTasks(meetingId)
      .then(setTasks)
      .catch((e) => setError(e.message));
  }, [meetingId]);

  useEffect(load, [load]);

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    setError("");
    try {
      await api.addLeadTask(meetingId, text.trim(), due ? new Date(due).toISOString() : null);
      setText("");
      setDue("");
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const patch = async (
    id: number,
    body: { text?: string; due_at?: string | null; done?: boolean },
  ) => {
    setError("");
    try {
      await api.patchLeadTask(meetingId, id, body);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const remove = async (id: number) => {
    if (!confirm("Удалить задачу?")) return;
    setError("");
    try {
      await api.deleteLeadTask(meetingId, id);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const open = tasks.filter((t) => !t.done_at);
  const done = tasks.filter((t) => t.done_at);

  return (
    <>
      <h3>
        Что сделать{" "}
        {open.length > 0 && <span className="badge lime">{open.length}</span>}
      </h3>

      {error && <div className="banner red">{error}</div>}

      <form onSubmit={add} className="touch-form">
        <input
          placeholder="Например: выслать прайс на СТМ"
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={500}
        />
        <input
          type="date"
          value={due}
          onChange={(e) => setDue(e.target.value)}
          title="Срок — можно не ставить"
        />
        <button className="primary" disabled={busy || !text.trim()}>
          Добавить
        </button>
      </form>

      {open.length === 0 && done.length === 0 ? (
        <p className="muted small">
          Задач нет. Всё, что сервис услышал в разговоре как обещание с нашей
          стороны, попадает сюда само.
        </p>
      ) : (
        <ul className="list-clean">
          {open.map((t) => {
            const label = t.due_at ? dueLabel(t.due_at) : null;
            return (
              <li key={t.id} className="task">
                <input
                  type="checkbox"
                  checked={false}
                  onChange={() => patch(t.id, { done: true })}
                  title="Отметить сделанным"
                />
                <span style={{ flex: 1 }}>{t.text}</span>
                {label && <span className={`badge ${label.cls}`}>{label.text}</span>}
                <input
                  type="date"
                  className="task-due"
                  value={toDateInput(t.due_at)}
                  onChange={(e) =>
                    patch(t.id, {
                      due_at: e.target.value ? new Date(e.target.value).toISOString() : null,
                    })
                  }
                  title="Изменить срок"
                />
                <button className="sm ghost" onClick={() => remove(t.id)}>
                  Удалить
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {done.length > 0 && (
        <>
          <button
            className="sm ghost"
            style={{ marginTop: 10 }}
            onClick={() => setShowDone((v) => !v)}
          >
            {showDone ? "Скрыть сделанное" : `Сделано: ${done.length}`}
          </button>
          {showDone && (
            <ul className="list-clean">
              {done.map((t) => (
                <li key={t.id} className="task done">
                  <input
                    type="checkbox"
                    checked
                    onChange={() => patch(t.id, { done: false })}
                    title="Вернуть в работу"
                  />
                  <span style={{ flex: 1 }}>{t.text}</span>
                  <span className="muted small nowrap">
                    {new Date(t.done_at!).toLocaleDateString("ru-RU", {
                      day: "numeric",
                      month: "short",
                    })}
                    {t.done_by_name ? ` · ${t.done_by_name}` : ""}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </>
  );
}
